import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { I18N as I18N_DEFAULT } from "@/data/site";

const LANGS = ["pt", "en", "es"];

// Walk PT structure to extract all leaf paths
function extractPaths(obj, prefix = []) {
  const paths = [];
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    for (const k of Object.keys(obj)) {
      paths.push(...extractPaths(obj[k], [...prefix, k]));
    }
  } else if (typeof obj === "string") {
    paths.push(prefix);
  }
  return paths;
}

function getAt(obj, path) {
  return path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : ""), obj);
}

function setAt(obj, path, value) {
  const copy = JSON.parse(JSON.stringify(obj));
  let cur = copy;
  for (let i = 0; i < path.length - 1; i++) {
    if (!cur[path[i]] || typeof cur[path[i]] !== "object") cur[path[i]] = {};
    cur = cur[path[i]];
  }
  cur[path[path.length - 1]] = value;
  return copy;
}

// Deep-merge remote i18n on top of built-in defaults so newly introduced keys
// show up in the editor immediately with their PT default pre-filled.
function mergeI18n(defaults, remote) {
  if (!remote || typeof remote !== "object") return JSON.parse(JSON.stringify(defaults));
  const out = {};
  for (const lang of Object.keys(defaults)) {
    out[lang] = deepMerge(defaults[lang], remote[lang]);
  }
  return out;
}
function deepMerge(a, b) {
  if (b === undefined || b === null) return JSON.parse(JSON.stringify(a));
  if (typeof a !== "object" || Array.isArray(a) || typeof b !== "object" || Array.isArray(b)) {
    return b === "" ? a : b;
  }
  const out = { ...a };
  for (const k of Object.keys(b || {})) {
    out[k] = deepMerge(a?.[k], b[k]);
  }
  return out;
}

export default function I18nEditor() {
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    api.get("/content/kv/i18n").then(({ data: d }) => {
      // Merge remote i18n with built-in defaults so newly introduced keys appear
      // as editable rows immediately, without requiring a manual DB seed.
      const merged = mergeI18n(I18N_DEFAULT, d);
      setData(merged);
      setOriginal(JSON.parse(JSON.stringify(merged)));
    }).finally(() => setLoading(false));
  }, []);

  const paths = useMemo(() => (data?.pt ? extractPaths(data.pt) : (I18N_DEFAULT.pt ? extractPaths(I18N_DEFAULT.pt) : [])), [data]);

  const groups = useMemo(() => {
    const g = {};
    paths.forEach(p => { const key = p[0]; (g[key] = g[key] || []).push(p); });
    return g;
  }, [paths]);

  const setField = (lang, path, value) => {
    setData(d => ({ ...d, [lang]: setAt(d[lang] || {}, path, value) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/i18n", { value: data });
      setOriginal(JSON.parse(JSON.stringify(data)));
      toast.success("Traduções salvas.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };
  const reset = () => setData(JSON.parse(JSON.stringify(original)));
  const isDirty = JSON.stringify(data) !== JSON.stringify(original);

  if (loading) return <div className="text-sm text-neutral-500">Carregando…</div>;
  if (!data) return <div className="text-sm text-neutral-500">Conteúdo de i18n vazio. Use &quot;Importar conteúdo inicial&quot; no Dashboard primeiro.</div>;

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div><h1 className="font-display text-4xl">Traduções</h1><div className="text-xs text-neutral-500 mt-1">Edite cada texto nos 3 idiomas lado a lado.</div></div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={!isDirty} className="text-xs px-3 py-2 border border-black/15 rounded-full disabled:opacity-40"><RotateCcw className="w-3 h-3 inline mr-1" /> Desfazer</button>
          <button onClick={save} disabled={!isDirty || saving} data-testid="i18n-save" className="text-xs px-4 py-2 bg-black text-white rounded-full disabled:opacity-40"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(groups).map(([groupKey, groupPaths]) => {
          const isOpen = openGroups[groupKey] !== false;
          return (
            <div key={groupKey} className="border border-black/10 rounded">
              <button onClick={() => setOpenGroups(g => ({...g, [groupKey]: !isOpen}))} className="w-full flex items-center justify-between p-3 hover:bg-black/[0.02]" data-testid={`i18n-group-${groupKey}`}>
                <span className="font-semibold capitalize flex items-center gap-2">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} {groupKey}</span>
                <span className="text-xs text-neutral-500">{groupPaths.length} campos</span>
              </button>
              {isOpen && (
                <div className="p-4 space-y-3 border-t border-black/10">
                  {groupPaths.map((path) => (
                    <div key={path.join(".")} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
                      <div className="text-xs text-neutral-500 font-mono pt-2">{path.join(".")}</div>
                      {LANGS.map(lang => (
                        <input key={lang} value={getAt(data[lang] || {}, path)} onChange={(e) => setField(lang, path, e.target.value)}
                          data-testid={`i18n-${path.join("-")}-${lang}`}
                          placeholder={lang.toUpperCase()}
                          className="border border-black/15 px-2 py-1.5 rounded text-sm" />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
